import type { Building } from "building-types/building/buildingTypes";
import type { CeilingPatchTable } from "building-types/ceilings/ceilingPatchTable";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { StairPlanes } from "building-types/stairs/stairTypesEnum";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable } from "common/data/table";
import objectMap from "common/utils/objectMap";
import { doorPlacementsOnLevel } from "doc/building/doorsOnLevel";
import { levelHasHalfStory } from "doc/building/levelHasHalfStory";
import { windowPlacementsOnLevel } from "doc/building/windowPlacementsOnLevel";
import { planeCopy } from "geom/plane";
import { WallDatumAbstractionProvider } from "../elements/datums/datumAbstractionProviders";
import { computeWallFoundationIntersections } from "../elements/foundation/foundationSectionUtils";
import { filterHalfStoryWindows, trimWallSystemWithHalfStoryRoofs } from "../elements/walls/halfStories";
import { makeWallCutHalfEdgeMaps } from "../elements/walls/wallCut";
import { makeWallSystem } from "../elements/walls/wallSystem";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import type { StairBottomOutlineAndPlane } from "../utils/stairTrims";
import { repairStairTrimmedWallFaces, trimWallSystemWithStairs } from "../utils/stairTrims";

export function selectGenerateWalls({
  levels,
  levelsAttributes,
  datumAbstractions,
  doorPlacements,
  roofSystems,
  windowPlacements,
  wallMasses,
  wallCuts,
  rooms,
  blocks,
  stairSystems,
  settings,
  planarGraphPerimeters,
  foundationHorizontalAssemblies,
  mirror,
  enabledFeatures,
}: Building) {
  const levelIsHalfStory = objectMap(levels, (level) => levelHasHalfStory(level.id, blocks));
  const blockIsHalfStory = objectMap(blocks, (x) => x.isHalfStory === true);

  return {
    levels,
    levelsAttributes,
    doorPlacements,
    windowPlacements,
    wallCuts,
    wallMasses,
    roofSystems,
    stairSystems,
    rooms,
    levelIsHalfStory,
    blockIsHalfStory,
    wallSettings: settings.wall,
    wallCatalogSettings: settings.wallCatalog,
    datumAbstractions,
    planarGraphPerimeters,
    foundationHorizontalAssemblies,
    mirror,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof selectGenerateWalls>({
  levelIsHalfStory: "shallow",
  blockIsHalfStory: "shallow",
  enabledFeatures: "shallow",
});

function generate(
  params: ReturnType<typeof selectGenerateWalls>,
): Pick<Building, "doorPlacements" | "windowPlacements" | "walls" | "ceilingPatchTable" | "issues"> {
  const issues: BuildingGenerationIssue[] = [];
  const {
    levels,
    levelsAttributes,
    datumAbstractions,
    doorPlacements,
    windowPlacements,
    wallCuts,
    roofSystems,
    wallMasses,
    levelIsHalfStory,
    stairSystems,
    wallCatalogSettings,
    mirror,
    foundationHorizontalAssemblies,
    enabledFeatures,
  } = params;

  const ceilingPatchTable: CeilingPatchTable = {};

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);

  // generate walls
  const walls: RWTable<WallSystem> = {};

  for (const levelId in levels) {
    const hasHalfStory = levelIsHalfStory[levelId];

    // filter windows if necessary
    if (roofSystems && hasHalfStory) {
      filterHalfStoryWindows(levelId, wallMasses[levelId], params);
    }

    // Compute foundation wall cuts BEFORE makeWallCutHalfEdgeMaps so they integrate
    // into the standard wall cut system
    const hasFoundations = foundationHorizontalAssemblies && Object.keys(foundationHorizontalAssemblies).length > 0;
    const foundationWallCuts = hasFoundations
      ? computeWallFoundationIntersections({
          foundations: foundationHorizontalAssemblies,
          level: levels[levelId],
          wallMasses: wallMasses[levelId],
        })
      : undefined;

    const [halfEdgeToWindowSet, halfEdgeToDoorSet, halfEdgeToWallCutSet] = makeWallCutHalfEdgeMaps(
      doorPlacementsOnLevel(levelId, doorPlacements),
      windowPlacementsOnLevel(levelId, windowPlacements),
      wallCuts[levelId],
    );

    // Merge foundation wall cuts into the wall cut set
    if (foundationWallCuts) {
      for (const [halfEdgeIndex, cuts] of foundationWallCuts) {
        if (!halfEdgeToWallCutSet[halfEdgeIndex]) {
          halfEdgeToWallCutSet[halfEdgeIndex] = [];
        }
        halfEdgeToWallCutSet[halfEdgeIndex].push(...cuts);
      }
    }

    // generate walls
    const levelWalls = makeWallSystem({
      wallDatumAbstractionProvider,
      wallMasses,
      halfEdgeToWindowSet,
      halfEdgeToDoorSet,
      halfEdgeToWallCutSet,
      wallCatalogSettings,
      levelId,
      mirror,
      issues,
    });

    // clip walls w/ stairs (wallPlanes gates to STAIR_CLIPPING-marked submasses)
    if (stairSystems) {
      for (const stairSystemId in stairSystems) {
        const { levelId: stairLevelId, wallTrimFaces, blocks } = stairSystems[stairSystemId];
        if (levelId !== stairLevelId) {
          continue;
        }

        const stairBottomPolyPlanes: StairBottomOutlineAndPlane[] = [];
        for (const blockId in blocks) {
          const block = blocks[blockId];
          const plane = block.planes[StairPlanes.BACK];
          if (!plane) {
            continue;
          }
          for (const stairPlanPoly of block.stairPlanPolys) {
            if (!stairPlanPoly?.outline) {
              continue;
            }
            stairBottomPolyPlanes.push({ outline: stairPlanPoly.outline, plane: planeCopy(plane) });
          }
        }

        trimWallSystemWithStairs(levelWalls, wallTrimFaces, stairBottomPolyPlanes, ceilingPatchTable, {
          gateByStairClippingOps: enabledFeatures.wallPlanes,
        });
      }

      repairStairTrimmedWallFaces(levelWalls, {
        gateByStairClippingOps: enabledFeatures.wallPlanes,
      });
    }

    walls[levelId] = levelWalls;

    // clip walls w/ half story if necessary
    if (roofSystems && hasHalfStory && !enabledFeatures.wallPlanes) {
      /**
       * @NOTE WALLS V2 - half story clipping is not supported for walls v2.
       * Half stories are being deprecated in favor of clipping operations on wall planes.
       */
      // can mutate the walls, the window and door collection
      trimWallSystemWithHalfStoryRoofs(levelId, levelWalls, params);
    }
  }

  return {
    walls,
    doorPlacements: { ...doorPlacements },
    windowPlacements: { ...windowPlacements },
    ceilingPatchTable,
    issues,
  };
}

export default memo(selectGenerateWalls, generate, compare);
